import {
  TbOutlineAlignLeft,
  TbOutlineBriefcase,
  TbOutlineBriefcase2,
  TbOutlineCategory,
  TbOutlineCurrencyEuro,
  TbOutlineId,
  TbOutlineLink,
  TbOutlineSchool,
  TbOutlineSparkles,
  TbOutlineWorld,
} from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import {
  originSuffix,
  SlotBadge,
  SuggestedMark,
} from "@/domains/freelance/taxonomy/components/sector-selector/primitives";
import { categoryIconClass } from "@/domains/freelance/taxonomy/constants/category-icons";
import {
  categoryLabel,
  parentOf,
  subcategoryLabel,
} from "@/domains/freelance/taxonomy/store/taxonomy-store";
import type { PickedSubcategory } from "@/domains/freelance/taxonomy/types";
import type { REMOTE_POLICY } from "@/domains/job-offer/create/constants";
import { cn } from "@/libs/cn";
import { formatCurrency } from "@/libs/format";
import {
  type OnboardingEducation,
  type OnboardingExperience,
  type SectorValue,
  useOnboardingStore,
} from "../../context/OnboardingContext";
import { sectorRefusalFor } from "../../utils/review-sector-error";
import { stepPosition } from "../../utils/step-position";
import {
  OnboardingReviewAvatar,
  OnboardingReviewCard,
  OnboardingReviewField,
  OnboardingReviewFieldList,
  OnboardingReviewIconAvatar,
  OnboardingReviewListItem,
} from "../shared/OnboardingReviewCard";
import { OnboardingDescription, OnboardingTitle } from "../shared/text";

const yearOf = (date: Date | string | null) =>
  date ? new Date(date).getFullYear().toString() : "";

const OnlinePresenceLink = (props: { href: string }) => (
  <a
    href={props.href}
    target="_blank"
    rel="noopener noreferrer"
    class="text-primary hover:underline"
  >
    {props.href}
  </a>
);

type SectorGroup = {
  category: string;
  slot: 0 | 1;
  subcategories: PickedSubcategory[];
};

/**
 * The sector as the review shows it (AC22): the primary and the secondary with
 * their subcategories as chips under each, each subcategory under its parent in
 * the taxonomy (one the tree does not know, or whose parent is neither sector,
 * falls under the primary). A suggested one keeps the signs of the selector and
 * of the profile card, a dashed `--ring` border and the sparkle, and says where
 * it came from in its accessible name.
 */
const SectorReviewCard = () => {
  const [store] = useOnboardingStore();
  const { t } = useI18n();

  const groups = createMemo<SectorGroup[]>(() => {
    const value: SectorValue = store.sector.value;
    const primary = value.job_category;
    const secondary = value.job_category_secondary;
    const fallback = primary ?? secondary;

    const ownerOf = (key: string) => {
      const parent = parentOf(key);
      return parent && (parent === primary || parent === secondary)
        ? parent
        : fallback;
    };

    const slots: [string | null, 0 | 1][] = [
      [primary, 0],
      [secondary, 1],
    ];
    return slots.flatMap(([category, slot]) =>
      category
        ? [
            {
              category,
              slot,
              subcategories: value.job_subcategories.filter(
                (subcategory) => ownerOf(subcategory.key) === category,
              ),
            },
          ]
        : [],
    );
  });

  return (
    <OnboardingReviewCard
      step="sector"
      icon={TbOutlineCategory}
      title={t("onboarding.steps.preview.sector")}
      subtitle={t("onboarding.steps.preview.step", {
        number: stepPosition("sector"),
      })}
      error={sectorRefusalFor(store.sector.value)}
    >
      <Show
        when={groups().length > 0}
        fallback={
          <p class="text-sm text-neutral-400">{t("taxonomy.sector.empty")}</p>
        }
      >
        <div class="flex flex-col gap-4">
          <For each={groups()}>
            {(group) => (
              <div class="flex flex-col gap-2">
                <div class="flex items-center gap-2 text-sm font-medium text-neutral-900">
                  <i
                    class={cn(
                      "ph text-base shrink-0",
                      categoryIconClass(group.category),
                    )}
                    aria-hidden="true"
                  />
                  <span class="min-w-0 wrap-break-word">
                    {categoryLabel(group.category)}
                  </span>
                  <SlotBadge index={group.slot} />
                </div>
                <Show when={group.subcategories.length > 0}>
                  <ul class="flex flex-wrap gap-1.5">
                    <For each={group.subcategories}>
                      {(subcategory) => (
                        <li
                          class={cn(
                            "inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs text-neutral-900",
                            subcategory.origin === "suggested"
                              ? "border-dashed border-ring bg-white"
                              : "border-neutral-300 bg-neutral-50",
                          )}
                        >
                          <Show when={subcategory.origin === "suggested"}>
                            <SuggestedMark />
                          </Show>
                          <span class="min-w-0 wrap-break-word">
                            {subcategoryLabel(subcategory.key)}
                          </span>
                          <Show when={subcategory.origin === "suggested"}>
                            <span class="sr-only">
                              {originSuffix(t, store.sector.source)}
                            </span>
                          </Show>
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>
              </div>
            )}
          </For>
        </div>
      </Show>
    </OnboardingReviewCard>
  );
};

/**
 * Every `<Show>` callback below returns **JSX**, never a bare string — and that
 * is a correctness rule here, not a style choice.
 *
 * `<Show>` invokes its callback child inside `untrack`, and its condition memo
 * compares with `equals: (a, b) => !a === !b`, so it does **not** re-run when
 * `when` goes from one truthy value to another (`"20"` → `"30"`). A callback
 * returning a string is evaluated once, on the spot, and the text freezes on the
 * first truthy value. Returning JSX escapes both: the compiler wraps each
 * interpolation in its own `insert(...)` effect, which tracks normally.
 * (Measured: string → stuck at 20, fragment and element → 30.)
 *
 * Today nothing here can go stale, because editing a section swaps this whole
 * component out — `<Review />` is the *fallback* of the `<Show>` in
 * `ManualOnboarding` / `LinkedInOnboarding`, so `EditStepView` replaces it and
 * it remounts with fresh values on the way back. That invariant is not local to
 * this file, though, and the same feature already edits pricing through a
 * **modal** on the LinkedIn hub screen — a modal does not unmount what is behind
 * it. Written this way, whichever editor wins later, the numbers stay true.
 */
export const Review = () => {
  const [store] = useOnboardingStore();
  const { t } = useI18n();

  const yearRange = (
    start: Date | string | null,
    end: Date | string | null,
    isCurrent?: boolean,
  ) => {
    const from = yearOf(start);
    const to = isCurrent ? t("onboarding.steps.preview.present") : yearOf(end);
    if (!from && !to) return "";
    return `${from} — ${to}`;
  };

  const experienceSubtitle = (experience: OnboardingExperience) =>
    [
      experience.company_name,
      yearRange(
        experience.start_date,
        experience.end_date,
        !!experience.is_current,
      ),
    ]
      .filter(Boolean)
      .join(" · ");

  const educationSubtitle = (education: OnboardingEducation) =>
    [
      education.field_of_study || education.degree_type,
      yearRange(education.start_date, education.end_date),
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <div class="flex flex-col gap-8 w-full">
      <div class="flex flex-col gap-4">
        <OnboardingTitle>{t("onboarding.steps.preview.title")}</OnboardingTitle>
        <OnboardingDescription>
          {t("onboarding.steps.preview.description")}
        </OnboardingDescription>
      </div>

      <div class="flex flex-col gap-4">
        <SectorReviewCard />

        <OnboardingReviewCard
          step="skills"
          icon={TbOutlineSparkles}
          title={t("onboarding.steps.preview.skills")}
          subtitle={`${t("onboarding.steps.preview.step", { number: stepPosition("skills") })} · ${t("onboarding.steps.preview.skillsCount", { count: store.skills.value.length })}`}
        >
          <Show
            when={store.skills.value.length}
            fallback={
              <p class="text-sm text-neutral-400">
                {t("onboarding.steps.preview.noSkills")}
              </p>
            }
          >
            <div class="flex flex-wrap gap-2">
              <For each={store.skills.value}>
                {(skill) => <Badge variant="outline">{skill}</Badge>}
              </For>
            </div>
          </Show>
        </OnboardingReviewCard>

        <OnboardingReviewCard
          step="job_title"
          icon={TbOutlineBriefcase2}
          title={t("onboarding.steps.preview.jobTitle")}
          subtitle={t("onboarding.steps.preview.step", {
            number: stepPosition("job_title"),
          })}
        >
          <p class="text-sm font-medium text-neutral-800">
            {store.job_title.value}
          </p>
        </OnboardingReviewCard>

        <OnboardingReviewCard
          step="experience"
          icon={TbOutlineBriefcase}
          title={t("onboarding.steps.preview.experience")}
          subtitle={`${t("onboarding.steps.preview.step", { number: stepPosition("experience") })} · ${t("onboarding.steps.preview.experienceCount", { count: store.experience.value.length })}`}
        >
          <Show
            when={store.experience.value.length}
            fallback={
              <p class="text-sm text-neutral-400">
                {t("onboarding.steps.preview.noExperience")}
              </p>
            }
          >
            <div class="divide-y divide-neutral-100">
              <For each={store.experience.value}>
                {(experience) => (
                  <OnboardingReviewListItem
                    leading={
                      <OnboardingReviewAvatar name={experience.company_name} />
                    }
                    title={experience.job_title}
                    subtitle={experienceSubtitle(experience)}
                  />
                )}
              </For>
            </div>
          </Show>
        </OnboardingReviewCard>

        <OnboardingReviewCard
          step="education"
          icon={TbOutlineSchool}
          title={t("onboarding.steps.preview.education")}
          subtitle={`${t("onboarding.steps.preview.step", { number: stepPosition("education") })} · ${t("onboarding.steps.preview.educationCount", { count: store.education.value.length })}`}
        >
          <Show
            when={store.education.value.length}
            fallback={
              <p class="text-sm text-neutral-400">
                {t("onboarding.steps.preview.noEducation")}
              </p>
            }
          >
            <div class="divide-y divide-neutral-100">
              <For each={store.education.value}>
                {(education) => (
                  <OnboardingReviewListItem
                    leading={
                      <OnboardingReviewIconAvatar>
                        <TbOutlineSchool size={18} />
                      </OnboardingReviewIconAvatar>
                    }
                    title={education.institution}
                    subtitle={educationSubtitle(education)}
                  />
                )}
              </For>
            </div>
          </Show>
        </OnboardingReviewCard>

        <OnboardingReviewCard
          step="locale"
          icon={TbOutlineWorld}
          title={t("onboarding.steps.preview.locale")}
          subtitle={`${t("onboarding.steps.preview.step", { number: stepPosition("locale") })} · ${t("onboarding.steps.preview.languagesCount", { count: store.locale.value.spoken_languages.length })}`}
        >
          <Show
            when={store.locale.value.spoken_languages.length}
            fallback={
              <p class="text-sm text-neutral-400">
                {t("onboarding.steps.preview.noLanguages")}
              </p>
            }
          >
            <div class="flex flex-wrap gap-2">
              <For each={store.locale.value.spoken_languages}>
                {(language) => <Badge variant="outline">{language.name}</Badge>}
              </For>
            </div>
          </Show>
        </OnboardingReviewCard>

        <OnboardingReviewCard
          step="summary"
          icon={TbOutlineAlignLeft}
          title={t("onboarding.steps.preview.summary")}
          subtitle={t("onboarding.steps.preview.step", {
            number: stepPosition("summary"),
          })}
        >
          <Show
            when={store.summary.value}
            fallback={
              <p class="text-sm text-neutral-400">
                {t("onboarding.steps.preview.noSummary")}
              </p>
            }
          >
            <p class="text-sm leading-relaxed text-neutral-700">
              {store.summary.value}
            </p>
          </Show>
        </OnboardingReviewCard>

        <OnboardingReviewCard
          step="pricing"
          icon={TbOutlineCurrencyEuro}
          title={t("onboarding.steps.preview.pricing")}
          subtitle={t("onboarding.steps.preview.step", {
            number: stepPosition("pricing"),
          })}
        >
          <OnboardingReviewFieldList>
            <OnboardingReviewField
              label={t("onboarding.steps.preview.availability")}
            >
              {store.info.value.is_available
                ? t("availability.available")
                : t("availability.unavailable")}
            </OnboardingReviewField>
            <OnboardingReviewField
              label={t("onboarding.steps.preview.weeklyHours")}
            >
              {/*
                The fragment is load-bearing — see the note above `Review`.
              */}
              <Show when={store.info.value.weekly_hours}>
                {(hours) => (
                  <>
                    {hours()} {t("availability.h_weekly_hours")}
                  </>
                )}
              </Show>
            </OnboardingReviewField>
            <OnboardingReviewField
              label={t("onboarding.steps.preview.dailyRate")}
            >
              {formatCurrency(store.pricing.value.price_per_day)}
            </OnboardingReviewField>
            <OnboardingReviewField
              label={t("onboarding.steps.preview.workPreference")}
            >
              <Show when={store.info.value.work_preference}>
                {(preference) => (
                  <>
                    {t(
                      `common.remote_policy_options.${preference() as (typeof REMOTE_POLICY)[number]}`,
                    )}
                  </>
                )}
              </Show>
            </OnboardingReviewField>
          </OnboardingReviewFieldList>
        </OnboardingReviewCard>

        <OnboardingReviewCard
          step="info"
          icon={TbOutlineId}
          title={t("onboarding.steps.preview.info")}
          subtitle={t("onboarding.steps.preview.step", {
            number: stepPosition("info"),
          })}
        >
          <OnboardingReviewFieldList>
            <OnboardingReviewField
              label={t("onboarding.steps.preview.residency")}
            >
              {store.info.value.residency}
            </OnboardingReviewField>
            <OnboardingReviewField
              label={t("onboarding.steps.preview.dateOfBirth")}
            >
              <Show when={store.info.value.date_of_birth}>
                {(date) => <>{new Date(date()).toLocaleDateString("it-IT")}</>}
              </Show>
            </OnboardingReviewField>
            <OnboardingReviewField label={t("onboarding.steps.preview.phone")}>
              {store.info.value.phone_number}
            </OnboardingReviewField>
          </OnboardingReviewFieldList>
        </OnboardingReviewCard>

        <OnboardingReviewCard
          step="online-presence"
          icon={TbOutlineLink}
          title={t("onboarding.steps.preview.onlinePresence")}
          subtitle={t("onboarding.steps.preview.step", {
            number: stepPosition("online-presence"),
          })}
        >
          <OnboardingReviewFieldList>
            <OnboardingReviewField
              label={t("onboarding.steps.preview.website")}
            >
              <Show when={store.online_presence.value.website}>
                {(href) => <OnlinePresenceLink href={href()} />}
              </Show>
            </OnboardingReviewField>
            <OnboardingReviewField
              label={t("onboarding.steps.preview.portfolio")}
            >
              <Show when={store.online_presence.value.portfolio}>
                {(href) => <OnlinePresenceLink href={href()} />}
              </Show>
            </OnboardingReviewField>
            <OnboardingReviewField label={t("onboarding.steps.preview.github")}>
              <Show when={store.online_presence.value.github}>
                {(href) => <OnlinePresenceLink href={href()} />}
              </Show>
            </OnboardingReviewField>
            <OnboardingReviewField
              label={t("onboarding.steps.preview.linkedin")}
            >
              <Show when={store.online_presence.value.linkedin}>
                {(href) => <OnlinePresenceLink href={href()} />}
              </Show>
            </OnboardingReviewField>
            <OnboardingReviewField
              label={t("onboarding.steps.preview.instagram")}
            >
              <Show when={store.online_presence.value.instagram}>
                {(href) => <OnlinePresenceLink href={href()} />}
              </Show>
            </OnboardingReviewField>
          </OnboardingReviewFieldList>
        </OnboardingReviewCard>
      </div>
    </div>
  );
};
